import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Lock, Key, Database, Eye, CheckCircle, MessageSquare } from 'lucide-react';

export default function Security() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="bg-blue-600 p-1.5 rounded">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-gray-900">
                IcebreakChat
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/#pricing" className="text-gray-600 hover:text-gray-900 hidden md:block">Pricing</a>
              <Button variant="ghost" onClick={() => navigate('/login')}>Log in</Button>
              <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
                Sign up free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-6">Security</h1>
        <p className="text-gray-600 mb-8">
          Your security and privacy are our top priorities. Learn how we protect your data.
        </p>

        <div className="space-y-8">
          {/* Data Encryption */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-semibold">Data Encryption</h2>
            </div>
            <p className="text-gray-700 mb-3">
              All data transmitted between your browser and our servers is encrypted using industry-standard TLS/SSL protocols.
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>End-to-end encryption for all communications</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Encrypted data storage at rest</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Regular security audits and updates</span>
              </li>
            </ul>
          </div>

          {/* Authentication */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Key className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-semibold">Authentication & Access Control</h2>
            </div>
            <p className="text-gray-700 mb-3">
              We implement robust authentication mechanisms to ensure only authorized users can access your account.
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Secure password hashing (bcrypt)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>JWT-based authentication tokens</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Role-based access control</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Session management and timeout</span>
              </li>
            </ul>
          </div>

          {/* Data Protection */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-semibold">Data Protection</h2>
            </div>
            <p className="text-gray-700 mb-3">
              Your data is protected through multiple layers of security controls.
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Regular automated backups</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Data isolation between accounts</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Secure data deletion on request</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>GDPR and privacy compliance</span>
              </li>
            </ul>
          </div>

          {/* Infrastructure Security */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-semibold">Infrastructure Security</h2>
            </div>
            <p className="text-gray-700 mb-3">
              Our infrastructure is built on trusted cloud providers with industry-leading security standards.
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>DDoS protection and mitigation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Firewall and intrusion detection</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>24/7 system monitoring</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Regular security patches and updates</span>
              </li>
            </ul>
          </div>

          {/* Monitoring & Response */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-semibold">Monitoring & Incident Response</h2>
            </div>
            <p className="text-gray-700 mb-3">
              We continuously monitor our systems and have procedures in place to respond to security incidents.
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Real-time threat detection</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Security incident response team</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Audit logging and forensics</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Prompt notification of security issues</span>
              </li>
            </ul>
          </div>

          {/* Report Security Issue */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-3">Report a Security Issue</h2>
            <p className="text-gray-700 mb-4">
              If you discover a security vulnerability, please report it to us immediately. We take all security reports seriously and will respond promptly.
            </p>
            <p className="text-gray-700">
              Email:{' '}
              <a href="mailto:security@icebreakchat.com" className="text-blue-600 hover:underline font-medium">
                security@icebreakchat.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-gray-50 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-600 p-1.5 rounded">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-semibold">IcebreakChat</span>
              </div>
              <p className="text-gray-600 text-sm">AI-powered customer support for e-commerce businesses.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/#features" className="hover:text-blue-600">Features</a></li>
                <li><a href="/#pricing" className="hover:text-blue-600">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/" className="hover:text-blue-600">Home</a></li>
                <li><a href="/login" className="hover:text-blue-600">Login</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/privacy" className="hover:text-blue-600">Privacy</a></li>
                <li><a href="/terms" className="hover:text-blue-600">Terms</a></li>
                <li><a href="/security" className="hover:text-blue-600">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 text-center text-sm text-gray-600">
            <p>&copy; 2025 IcebreakChat. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
